import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Avatar,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  type AlertColor,
  type AlertPropsColorOverrides,
  InputAdornment,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import PageBaseLayout from '../layouts/PageBaseLayout';
import { Controller, useForm } from 'react-hook-form';
import type { OverridableStringUnion } from '@mui/types';
import useBackdrop from '../hooks/useBackdrop';
import useSnackbar from '../hooks/useSnackbar';
import { supabase } from '../supabase-client';
import { AuthContext } from '../contexts/AuthContext';

interface ProfileData {
  businessName: string;
  businessDescription: string;
  businessLogo: string; // URL or base64
  category: string;
}

const CATEGORY_OPTIONS = [
  'Food & Beverage',
  'Retail',
  'Health & Wellness',
  'Education',
  'Technology',
  'Entertainment',
  'Other',
];

type FormValues = {
  password: string;
};

const defaultValues: FormValues = {
  password: '',
};

export default function Profile() {
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const authCtx = useContext(AuthContext);
  // Mock initial data, replace with real data fetching in production
  const [profile, setProfile] = useState<ProfileData>({
    businessName: '',
    businessDescription: '',
    businessLogo: '', // Placeholder or fetched logo URL
    category: '',
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(profile);
  const [logoPreview, setLogoPreview] = useState(profile.businessLogo);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<{ value: unknown }>
      | any,
  ) => {
    setForm((prev) => ({ ...prev, category: event.target.value as string }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
        setForm((prev) => ({ ...prev, businessLogo: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEdit = () => {
    setForm(profile);
    setLogoPreview(profile.businessLogo);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setForm(profile);
    setLogoPreview(profile.businessLogo);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setProfile(form);
    setEditing(false);
  };

  const onSubmit = async (formData: FormValues) => {
    console.log(formData);
    let message = 'Password has been updated.';
    let status:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined = 'success';

    backdrop.handleBackdropOpen();
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: formData.password,
      });

      if (error) {
        throw error;
      }

      reset();
    } catch (error) {
      const errorCast = error as Error;
      console.error(errorCast);
      message = errorCast.message;
      status = 'error';
    }
    snackbar.handleSnackbarOpen(message, status);
    backdrop.handleBackdropClose();
  };

  const getData = async () => {
    backdrop.handleBackdropOpen();

    try {
      const response = await supabase
        .from('merchants_view')
        .select('*')
        .eq('id', authCtx.merchantId)
        .single();

      if (response.error) {
        throw new Error(response.error.message);
      }

      setProfile({
        businessName: response.data?.name || '',
        businessDescription: response.data?.caption || '',
        businessLogo: response.data?.logo_url || '',
        category: response.data?.category || '',
      });
    } catch (error) {
      snackbar.handleSnackbarOpen(String(error), 'error');
    }

    backdrop.handleBackdropClose();
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <PageBaseLayout title="Profile">
      {!editing ? (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Brand Name
            </Typography>
            <Typography>{profile.businessName}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Description
            </Typography>
            <Typography>{profile.businessDescription}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Category
            </Typography>
            <Typography>{profile.category}</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Logo
            </Typography>
            <Box mt={1}>
              {profile.businessLogo ? (
                <Avatar
                  src={profile.businessLogo}
                  alt="Business Logo"
                  sx={{ width: 100, height: 100, borderRadius: 2 }}
                  variant="rounded"
                />
              ) : (
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    bgcolor: '#eee',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Typography color="text.secondary">No Logo</Typography>
                </Box>
              )}
            </Box>
          </Box>
          {/* <Button variant="contained" onClick={handleEdit}>
            Edit Profile
          </Button> */}
        </Stack>
      ) : (
        <form onSubmit={handleSave}>
          <Stack spacing={3}>
            <TextField
              label="Business Name"
              name="businessName"
              value={form.businessName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Description"
              name="businessDescription"
              value={form.businessDescription}
              onChange={handleChange}
              required
              fullWidth
              multiline
              minRows={4}
            />
            <FormControl fullWidth required>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                label="Category"
                name="category"
                value={form.category}
                onChange={handleCategoryChange}>
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Logo:
              </Typography>
              <Button variant="outlined" component="label">
                Upload Logo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleLogoChange}
                />
              </Button>
              {logoPreview && (
                <Avatar
                  src={logoPreview}
                  alt="Logo Preview"
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    mt: 2,
                  }}
                  variant="rounded"
                />
              )}
            </Box>
            <Box>
              <Button type="submit" variant="contained" sx={{ mr: 2 }}>
                Save
              </Button>
              <Button type="button" variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      )}
    </PageBaseLayout>
  );
}
